import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { resolveBrand } from "@/config/brand";
import { clearNeedsWorkspacePick } from "@/lib/workspacePick";
import {
  readTabWorkspace,
  readWorkspaceFromUrl,
  resolveActiveWorkspace,
  stripWorkspaceParamFromUrl,
  writeTabWorkspace,
} from "@/lib/active-workspace";

interface Agent {
  id: string;
  organization_id: string;
  user_id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
  availability_status: string;
  current_status_id?: string | null;
}

export type SetAgentStatusResult = {
  ok: boolean;
  code?: string;
  message?: string;
  open_count?: number;
  availability_status?: string;
  status_id?: string;
  categoria?: string;
};

interface Workspace {
  organization_id: string;
  organization_name: string;
  agent_id: string;
  workspace_type?: string;
  is_associate?: boolean;
  is_delegate?: boolean;
  guia_enabled?: boolean;
  billing_managed_by_parent?: boolean;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  agent: Agent | null;
  organizationName: string | null;
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  subscriptionStatus: string | null;
  userRole: string | null;
  billingManagedByAgency: boolean;
  isMatrixDelegate: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  updateAvailability: (status: "online" | "offline" | "busy") => Promise<void>;
  updateAgentStatus: (statusId: string) => Promise<SetAgentStatusResult>;
  switchWorkspace: (organizationId: string, redirectTo?: string) => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  agent: null,
  organizationName: null,
  workspaces: [],
  activeWorkspaceId: null,
  subscriptionStatus: null,
  userRole: null,
  billingManagedByAgency: false,
  isMatrixDelegate: false,
  loading: true,
  signOut: async () => {},
  updateAvailability: async () => {},
  updateAgentStatus: async () => ({ ok: false }),
  switchWorkspace: async () => {},
  refreshWorkspaces: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [billingManagedByAgency, setBillingManagedByAgency] = useState(false);
  const [isMatrixDelegate, setIsMatrixDelegate] = useState(false);
  const [loading, setLoading] = useState(true);

  const currentBrand = resolveBrand();

  const fetchAgent = async (userId: string) => {
    // Fetch all agents for this user (multi-workspace)
    const { data: agents } = await supabase
      .from("agents")
      .select("*")
      .eq("user_id", userId);

    if (!agents || agents.length === 0) {
      // No agents found — do NOT auto-create orgs.
      // Account provisioning is handled by register-flow and invite-agent only.
      setAgent(null);
      setWorkspaces([]);
      setActiveWorkspaceId(null);
      setBillingManagedByAgency(false);
      setIsMatrixDelegate(false);
      writeTabWorkspace(null);
      setLoading(false);
      return;
    }

    // Fetch org names and brand for all agents
    const orgIds = [...new Set(agents.map(a => a.organization_id))];
    const { data: orgs } = await supabase
      .from("organizations")
      .select("id, name, brand, workspace_type, parent_organization_id, guia_enabled")
      .in("id", orgIds);

    // Filter only orgs matching the current brand
    const brandOrgs = orgs?.filter(o => o.brand === currentBrand.id) || [];

    const orgMap: Record<string, string> = {};
    brandOrgs.forEach(o => { orgMap[o.id] = o.name; });

    // Filter agents to only those in matching brand orgs.
    // Prefer non-support agents for the workspace list, but fallback to support agents
    // when the user only has support access for this brand (production super-admin case).
    const brandOrgIds = new Set(brandOrgs.map(o => o.id));
    const brandAgents = agents.filter(a => brandOrgIds.has(a.organization_id) && !a.is_support_agent);
    const allBrandAgents = agents.filter(a => brandOrgIds.has(a.organization_id));
    const workspaceAgents = brandAgents.length > 0 ? brandAgents : allBrandAgents;

    if (workspaceAgents.length === 0) {
      setAgent(null);
      setWorkspaces([]);
      setActiveWorkspaceId(null);
      setBillingManagedByAgency(false);
      setIsMatrixDelegate(false);
      writeTabWorkspace(null);
      return;
    }

    const orgMeta: Record<string, { workspace_type?: string; parent_organization_id?: string | null; guia_enabled?: boolean; billing_managed_by_parent?: boolean }> = {};
    brandOrgs.forEach(o => {
      const row = o as { id: string; workspace_type?: string; parent_organization_id?: string | null; guia_enabled?: boolean };
      orgMeta[row.id] = { workspace_type: row.workspace_type, parent_organization_id: row.parent_organization_id, guia_enabled: row.guia_enabled };
    });

    const associateIds = Object.entries(orgMeta)
      .filter(([, m]) => !!m.parent_organization_id)
      .map(([id]) => id);
    if (associateIds.length > 0) {
      const { data: managed } = await supabase
        .from("organizations")
        .select("id, billing_managed_by_parent")
        .in("id", associateIds);
      for (const m of (managed ?? []) as { id: string; billing_managed_by_parent?: boolean }[]) {
        if (orgMeta[m.id]) orgMeta[m.id].billing_managed_by_parent = !!m.billing_managed_by_parent;
      }
    }

    const parentIds = [...new Set(
      Object.values(orgMeta).map(m => m.parent_organization_id).filter((id): id is string => !!id && !orgMeta[id])
    )];
    if (parentIds.length > 0) {
      const { data: parents } = await supabase
        .from("organizations")
        .select("id, guia_enabled")
        .in("id", parentIds);
      if (parents) {
        for (const p of parents as { id: string; guia_enabled?: boolean }[]) {
          orgMeta[p.id] = { guia_enabled: p.guia_enabled };
        }
      }
    }

    const ws: Workspace[] = Array.from(
      new Map(
        workspaceAgents.map(a => [
          a.organization_id,
          {
            organization_id: a.organization_id,
            organization_name: orgMap[a.organization_id] || "Sin nombre",
            agent_id: a.id,
            workspace_type: orgMeta[a.organization_id]?.workspace_type,
            is_associate: !!orgMeta[a.organization_id]?.parent_organization_id,
            is_delegate: !!(a as { is_matrix_delegate?: boolean }).is_matrix_delegate,
            guia_enabled: !!orgMeta[a.organization_id]?.guia_enabled
              || (orgMeta[a.organization_id]?.parent_organization_id
                ? !!orgMeta[orgMeta[a.organization_id]!.parent_organization_id!]?.guia_enabled
                : false),
            billing_managed_by_parent: !!orgMeta[a.organization_id]?.billing_managed_by_parent,
          },
        ])
      ).values()
    );
    setWorkspaces(ws);

    // Determine active workspace: check user_active_organization first
    const { data: activeOrg } = await supabase
      .from("user_active_organization")
      .select("organization_id")
      .eq("user_id", userId)
      .maybeSingle();

    // Allow impersonation: if active org has a support agent, still allow it as
    // active workspace. Both the tab's own choice and the stored row are checked
    // against this list, so a stale or cross-brand value can never be applied.
    const allowedIds = [
      ...ws.map((w) => w.organization_id),
      ...allBrandAgents.map((a) => a.organization_id),
    ];

    // This tab's choice wins over the shared server row: the row is global per
    // user, so another tab writing it must not drag this one to a different
    // workspace on every token refresh. An explicit `?org=` outranks both: the
    // admin panel opens a workspace in a new tab, and that tab inherits a copy
    // of the opener's sessionStorage.
    const urlRequest = readWorkspaceFromUrl();
    const activeOrgId = resolveActiveWorkspace({
      urlRequest,
      tabChoice: readTabWorkspace(),
      storedOrgId: activeOrg?.organization_id ?? null,
      allowedIds,
      fallbackId: ws[0]?.organization_id ?? null,
    });
    if (urlRequest) stripWorkspaceParamFromUrl();

    // Sync user_active_organization to the workspace the user is actually accessing.
    // get_current_organization_id() (used by RLS on channels, conversations, etc.)
    // reads from this table, so leaving a stale cross-brand row here causes 403s on
    // INSERT/UPDATE when a multi-brand user lands on a different brand's host.
    if (activeOrgId && (!activeOrg || activeOrg.organization_id !== activeOrgId)) {
      if (activeOrg) {
        await supabase
          .from("user_active_organization")
          .update({ organization_id: activeOrgId, updated_at: new Date().toISOString() })
          .eq("user_id", userId);
      } else {
        await supabase
          .from("user_active_organization")
          .insert({ user_id: userId, organization_id: activeOrgId });
      }
    }

    setActiveWorkspaceId(activeOrgId || null);
    setBillingManagedByAgency(!!(activeOrgId && orgMeta[activeOrgId]?.billing_managed_by_parent));
    setIsMatrixDelegate(
      !!activeOrgId && agents.some(
        (a) => a.organization_id === activeOrgId && !!(a as { is_matrix_delegate?: boolean }).is_matrix_delegate,
      ),
    );
    // Remember it for this tab so the next auth event resolves to the same
    // workspace instead of whatever another tab last wrote.
    writeTabWorkspace(activeOrgId || null);

    // Mark invitation as active only when the user actually accesses this workspace
    if (activeOrgId) {
      const pendingAgent = agents.find(
        a => a.organization_id === activeOrgId && a.invitation_status === "pending"
      );

      if (pendingAgent) {
        await supabase
          .from("agents")
          .update({ invitation_status: "active" } as any)
          .eq("id", pendingAgent.id);

        pendingAgent.invitation_status = "active";
      }
    }

    // Check subscription status for active workspace
    if (activeOrgId) {
      const { data: sub } = await supabase
        .from("subscriptions")
        .select("status")
        .eq("organization_id", activeOrgId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      setSubscriptionStatus(sub?.status || null);

      // Fetch user role for active workspace
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("organization_id", activeOrgId)
        .maybeSingle();
      setUserRole(roleData?.role || null);
    } else {
      setSubscriptionStatus(null);
      setUserRole(null);
    }

    // Set the active agent
    const activeAgent = allBrandAgents.find(a => a.organization_id === activeOrgId)
      || workspaceAgents[0]
      || agents[0];
    setAgent(activeAgent as Agent);
    setOrganizationName(orgMap[activeAgent.organization_id] || null);
  };

  const switchWorkspace = useCallback(async (organizationId: string, redirectTo?: string) => {
    if (!user) return;

    // Validated write path: rejects orgs the user does not belong to.
    const { error } = await supabase.rpc("set_active_organization" as never, {
      _organization_id: organizationId,
    } as never);
    if (error) {
      console.error("switchWorkspace failed:", error.message);
      throw error;
    }

    setActiveWorkspaceId(organizationId);
    // Survives the reload below, and keeps this tab on this workspace even if
    // another tab switches to a different one.
    writeTabWorkspace(organizationId);

    // Full navigation refresh so every RLS-scoped query remounts on the new org.
    window.location.assign(redirectTo || `${window.location.pathname}${window.location.search}`);
  }, [user]);

  const refreshWorkspaces = useCallback(async () => {
    if (!user) return;
    await fetchAgent(user.id);
  }, [user]);

  // ── The focused tab owns the RLS pointer ──
  // PostgREST calls carry this tab's workspace in the x-active-org header, but
  // Realtime and the edge functions still resolve through the global
  // user_active_organization row, so a background tab could leave it pointing
  // elsewhere. Re-assert whenever this tab comes to the front — including a
  // plain window focus, since two windows visible side by side never fire
  // visibilitychange — so what the user sees and what those paths enforce agree
  // before they can act.
  useEffect(() => {
    if (!user || !activeWorkspaceId) return;

    const reassertActiveOrg = async () => {
      if (document.visibilityState !== "visible") return;

      const { data: current } = await supabase
        .from("user_active_organization")
        .select("id, organization_id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (current?.organization_id === activeWorkspaceId) return;

      if (current) {
        await supabase
          .from("user_active_organization")
          .update({ organization_id: activeWorkspaceId, updated_at: new Date().toISOString() })
          .eq("user_id", user.id);
      } else {
        await supabase
          .from("user_active_organization")
          .insert({ user_id: user.id, organization_id: activeWorkspaceId });
      }
    };

    document.addEventListener("visibilitychange", reassertActiveOrg);
    window.addEventListener("focus", reassertActiveOrg);
    return () => {
      document.removeEventListener("visibilitychange", reassertActiveOrg);
      window.removeEventListener("focus", reassertActiveOrg);
    };
  }, [user, activeWorkspaceId]);

  // ── SILENT TOKEN REFRESH: proactively refresh before expiry ──
  useEffect(() => {
    let refreshTimer: NodeJS.Timeout | null = null;

    const scheduleRefresh = (session: Session | null) => {
      if (refreshTimer) clearTimeout(refreshTimer);
      if (!session?.expires_at) return;

      // Refresh 5 minutes before expiry
      const expiresAt = session.expires_at * 1000; // convert to ms
      const now = Date.now();
      const refreshIn = Math.max(expiresAt - now - 5 * 60 * 1000, 30_000); // at least 30s

      refreshTimer = setTimeout(async () => {
        try {
          const { data, error } = await supabase.auth.refreshSession();
          if (error) {
            console.warn("Silent token refresh failed:", error.message);
            // Don't force logout — let onAuthStateChange handle it
          } else {
            console.log("🔄 Token refreshed silently");
            scheduleRefresh(data.session); // Schedule next refresh
          }
        } catch (e) {
          console.warn("Silent refresh error:", e);
        }
      }, refreshIn);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        scheduleRefresh(session);
        if (session?.user) {
          setTimeout(async () => {
            try {
              await fetchAgent(session.user.id);
            } catch (e) {
              console.error("Error fetching agent:", e);
            } finally {
              setLoading(false);
            }
          }, 0);
        } else {
          setAgent(null);
          setWorkspaces([]);
          setActiveWorkspaceId(null);
          setBillingManagedByAgency(false);
          setIsMatrixDelegate(false);
          writeTabWorkspace(null);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      scheduleRefresh(session);
      if (session?.user) {
        try {
          await fetchAgent(session.user.id);
        } catch (e) {
          console.error("Error fetching agent:", e);
        }
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, []);

  const signOut = async () => {
    clearNeedsWorkspacePick();
    try {
      await supabase.rpc("set_agent_offline" as never);
    } catch {
      // Best-effort: don't block sign-out if the RPC fails.
    }
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setAgent(null);
    setWorkspaces([]);
    setActiveWorkspaceId(null);
    setBillingManagedByAgency(false);
    setIsMatrixDelegate(false);
    writeTabWorkspace(null);
    setUserRole(null);
  };

  const updateAgentStatus = async (statusId: string): Promise<SetAgentStatusResult> => {
    if (!agent) return { ok: false, message: "Agente no encontrado" };

    const { data, error } = await supabase.rpc("set_my_agent_status" as never, {
      p_status_id: statusId,
    } as never);

    if (error) {
      return { ok: false, message: error.message };
    }

    const result = (data || { ok: false }) as SetAgentStatusResult;
    if (result.ok) {
      setAgent({
        ...agent,
        availability_status: result.availability_status || agent.availability_status,
        current_status_id: result.status_id || statusId,
      });
    }
    return result;
  };

  // ── Presence heartbeat: ping every 2 min so assign_from_queue knows we're alive ──
  useEffect(() => {
    if (!agent) return;

    const ping = () => {
      supabase.rpc("ping_presence" as never).then(() => {}, () => {});
    };

    ping();
    const interval = setInterval(ping, 2 * 60 * 1000);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") ping();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    const handleUnload = () => {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/set_agent_offline`;
      const token = session?.access_token;
      if (!url || !token) return;
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? "",
        },
        body: JSON.stringify({}),
        keepalive: true,
      }).catch(() => {});
    };

    window.addEventListener("pagehide", handleUnload);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handleUnload);
    };
  }, [agent, session]);

  const updateAvailability = async (status: "online" | "offline" | "busy") => {
    if (!agent) return;

    try {
      const { data, error } = await supabase.rpc("list_agent_statuses_for_workspace" as never, {
        p_workspace_id: activeWorkspaceId,
      } as never);
      if (error) throw error;

      const list = (data as unknown as { id: string; key: string; is_system: boolean }[]) || [];
      const match = list.find((s) => s.is_system && s.key === status);
      if (match) {
        const result = await updateAgentStatus(match.id);
        if (!result.ok && result.message) {
          // Caller (UI) may toast; keep legacy mirror only on hard failure below.
          return;
        }
        if (result.ok) return;
      }
    } catch {
      // Fall through to legacy update if RPC path is unavailable.
    }

    await supabase
      .from("agents")
      .update({ availability_status: status })
      .eq("id", agent.id);
    setAgent({ ...agent, availability_status: status });
  };

  return (
    <AuthContext.Provider value={{ 
      session, user, agent, organizationName, workspaces, activeWorkspaceId,
      subscriptionStatus, userRole, billingManagedByAgency, isMatrixDelegate, loading, signOut, updateAvailability, updateAgentStatus,
      switchWorkspace, refreshWorkspaces
    }}>
      {children}
    </AuthContext.Provider>
  );
}
